import { SaleLiteModel, SaleModel } from 'api-hooks/sale/common';
import { SaleMinimalModel } from 'api-hooks/sale/model';
import { useGetSales } from 'api-hooks/sale/query';
import { OnChangeParams, Option } from 'baseui/select';
import { transformLabel } from 'common/utils/string';
import { Input } from 'components/elements';
import useMultiSelectInputHelper from 'hooks/use-multi-select-input-helper';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { customerNameTransformer } from '../ticket-sale/hooks/use-get-default-values';

interface Props {
  name: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  onAfterChange?: (value?: OnChangeParams) => void;
  noMargin?: boolean;
  fieldType?: 'control' | 'uncontrol';
  disabled?: boolean;
  hasProject?: boolean;
}

export function salesMultiTransformer(
  item: SaleModel | SaleLiteModel | SaleMinimalModel,
): Option {
  return {
    id: item.refId,
    label: transformLabel([
      (item as any).invoiceNumber ?? (item as any).number,
      item.code,
      customerNameTransformer(item.customer),
    ]),
    extra: item,
  };
}

export default function SalesMultiInput(props: Props) {
  const { t } = useTranslation();
  const {
    label = t('common:invoice'),
    placeholder = t('common:select_invoice'),
    disabled,
    hasProject,
  } = props;

  const { control, setValue } = useFormContext<any>();

  const value = useWatch({ control, name: props.name });
  const { overrides, ...selectProps } = useMultiSelectInputHelper({
    value,
    onSelectItem: (value) => {
      setValue(props.name, value || []);
    },
    useListQueryHook: useGetSales,
    getMemoizedListVariables: React.useCallback(
      (page, q) => ({
        params: {
          q,
          page,
          filter: { status: 'active|completed' } as unknown as string,
          hasProject,
        },
      }),
      [hasProject],
    ),
    renderCreate: false,
    listTransformer: React.useCallback((result) => {
      return (result.data || []).map((v) => salesMultiTransformer(v));
    }, []),
    paginationTransformer: React.useCallback((result) => {
      return result.meta;
    }, []),
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin={props.noMargin}
      fieldType={props.fieldType}
      multi
      filterOptions={null}
      {...selectProps}
      disabled={disabled}
    />
  );
}
